import React, { useMemo, useState } from "react";
import { consoleApi } from "../../../services/consoleApiService";
import { useConsoleFetch } from "../../../hooks/useConsoleFetch";

const PILLAR_LABELS = {
  "problem-reveal": "Problem→Reveal",
  "feature-spotlight": "Feature",
  "club-spotlight": "Club",
  "comparison": "Comparison",
  "how-to": "How-to",
};

const PILLAR_OPTIONS = [
  { value: "", label: "All pillars" },
  { value: "problem-reveal", label: "Problem → Reveal" },
  { value: "feature-spotlight", label: "Feature Spotlight" },
  { value: "club-spotlight", label: "Club Spotlight" },
  { value: "comparison", label: "Comparison" },
  { value: "how-to", label: "How To" },
];

const PLATFORM_OPTIONS = [
  { value: "", label: "All platforms" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
];

const MOOD_SWATCH = {
  "night-tempo": "#787f9a",
  "steady-slate": "#3d4256",
  "verified-forest": "#3a6a4c",
  "warning-dusk": "#f59e0b",
};

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="ch-lib__lightbox" onClick={onClose}>
      <div className="ch-lib__lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <img src={item.url} alt={item.brief_topic || "render"} />
        <div className="ch-lib__lightbox-meta">
          <div className="ch-lib__lightbox-topic">{item.brief_topic}</div>
          <div className="ch-lib__lightbox-tags">
            <span className="content-hub__tag">{PILLAR_LABELS[item.pillar] || item.pillar}</span>
            <span className="content-hub__tag">{item.platform}</span>
            <span className="content-hub__tag">slide {item.slide_index}</span>
            <span className="content-hub__tag" style={{ background: MOOD_SWATCH[item.mood] || "var(--s3)" }}>
              {item.mood}
            </span>
          </div>
          <div className="ch-lib__lightbox-actions">
            <a href={item.url} target="_blank" rel="noreferrer" className="content-hub__btn">
              Open full size
            </a>
            <button type="button" className="content-hub__btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LibraryView({ onOpenBrief }) {
  const [filters, setFilters] = useState({ pillar: "", platform: "" });
  const [active, setActive] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (filters.pillar) p.pillar = filters.pillar;
    if (filters.platform) p.platform = filters.platform;
    return p;
  }, [filters]);

  const { data: items, loading, error } = useConsoleFetch(
    () => consoleApi.content.library(params).then((r) => (Array.isArray(r) ? r : [])),
    [params.pillar, params.platform],
    { refetchOnWindowFocus: false }
  );

  const list = Array.isArray(items) ? items : [];

  return (
    <div className="ch-lib">
      <div className="ch__toolbar">
        <div className="ch__toolbar-left">
          <div className="ch__toolbar-title">
            <span className="ch__toolbar-eyebrow">Library</span>
            <span className="ch__toolbar-count">{list.length} {list.length === 1 ? "render" : "renders"}</span>
          </div>
        </div>
        <div className="ch__filters">
          <select
            className="ch__select"
            value={filters.pillar}
            onChange={(e) => setFilters((f) => ({ ...f, pillar: e.target.value }))}
          >
            {PILLAR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="ch__select"
            value={filters.platform}
            onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}
          >
            {PLATFORM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(filters.pillar || filters.platform) && (
            <button
              type="button"
              className="ch__btn ch__btn--link"
              onClick={() => setFilters({ pillar: "", platform: "" })}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {loading && <p className="content-hub__hint">Loading renders…</p>}
      {error && <div className="content-hub__error">Failed to load library.</div>}
      {!loading && list.length === 0 && (
        <div className="content-hub__empty">
          <p>No renders yet. Render a brief to see its slides here.</p>
        </div>
      )}

      <div className="ch-lib__grid">
        {list.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ch-lib__card"
            onClick={() => setActive(item)}
            title={`${item.brief_topic} — slide ${item.slide_index}`}
          >
            <div className="ch-lib__thumb">
              <img src={item.url} alt="" loading="lazy" />
              <span
                className="ch-lib__mood-dot"
                style={{ background: MOOD_SWATCH[item.mood] || "#787f9a" }}
                title={item.mood}
              />
            </div>
            <div className="ch-lib__card-meta">
              <div className="ch-lib__card-topic">{item.brief_topic}</div>
              <div className="ch-lib__card-tags">
                <span className="content-hub__tag">{PILLAR_LABELS[item.pillar] || item.pillar}</span>
                <span className="content-hub__tag">{item.platform}</span>
                <span className="content-hub__tag content-hub__tag--soft">#{item.slide_index}</span>
              </div>
              {onOpenBrief && (
                <button
                  type="button"
                  className="ch-lib__open-brief"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenBrief(item.brief_id);
                  }}
                >
                  Open brief →
                </button>
              )}
            </div>
          </button>
        ))}
      </div>

      <Lightbox item={active} onClose={() => setActive(null)} />
    </div>
  );
}
